/**
 * Single-platform ("locked") sign-in (unified-access.md section 3.2,
 * fuel-pda.md section 2.1). A platform that must never expose the portal,
 * such as the Fuel PDA shell, sends the browser here with `lock=1` next to
 * its absolute `returnTo`. For the rest of this browser tab, Unified Access
 * then only shows the sign-in pages and always returns to that platform.
 *
 * The value kept is the platform's return address. It is not trusted: the
 * browser is only ever sent to the launch URL that `POST /launch/return`
 * resolves for it server-side, and an address the server does not know as a
 * platform drops the lock (lib/navigation.ts), so a crafted link cannot turn
 * the lock into an open redirect or keep a normal browser tab locked.
 *
 * `sessionStorage` scopes the lock to one tab; it survives the full page
 * loads between the platform and Unified Access. Storage can be unavailable
 * (private mode, blocked site data), so every access is guarded and an
 * in-memory copy keeps the current page load working.
 */
const STORAGE_KEY = 'ua_platform_lock';

let memory: string | null = null;

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** The locked platform's return address, or null when sign-in is not locked. */
export function getPlatformLock(): string | null {
  try {
    const stored = storage()?.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // Fall back to the in-memory copy.
  }
  return memory;
}

export function isPlatformLocked(): boolean {
  return getPlatformLock() !== null;
}

export function setPlatformLock(returnTo: string): void {
  memory = returnTo;
  try {
    storage()?.setItem(STORAGE_KEY, returnTo);
  } catch {
    // The in-memory copy still covers this page load.
  }
}

export function clearPlatformLock(): void {
  memory = null;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing else to clear.
  }
}

/** The return address to use: the locked platform wins over the URL's own `returnTo`. */
export function effectiveReturn(returnTo: string | undefined): string | undefined {
  return getPlatformLock() ?? returnTo;
}
